import { SubtreeController } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import { loadCountries } from "../../core/ops/geo-ops";
import { createLocation, updateLocation } from "../../core/ops/location-ops";
import { hasErrors } from "../../core/primitives/form-errors";
import { busyForm, failedForm, idleForm } from "../../core/primitives/load-status";
import {
  draftToLocationUpdate,
  draftToNewLocation,
  EMPTY_LOCATION,
  type LocationField,
  locationDraft,
  validateLocationDraft,
} from "../../core/primitives/location-form";
import { OnboardingLocationsActions, OnboardingLocationsState } from "./onboarding-locations-model";

export type OnboardingLocationsDeps = Pick<
  AppDeps,
  "client" | "authHandler" | "storeRepository" | "locationsRepository" | "countriesRepository"
>;

export interface OnboardingLocationsParams {
  /** `?claimOnboarding=1`; a walkthrough only when `storeId` names the selected shop. */
  claimOnboarding: boolean;
  storeIdParam: string | null;
}

export interface OnboardingLocationsRouting {
  toPrevious(storeId: string, claimWalkthrough: boolean): void;
  toWebsite(storeId: string, claimWalkthrough: boolean): void;
}

/**
 * Step 2: where the shop sells. Each location is committed as it is added, not
 * batched at Continue: that is what makes the step resumable, and what starts the
 * geocoding the "Finding on map…" badge reports on. The shell reloads the list
 * after every write and polls while a branch awaits its coordinates.
 */
export class OnboardingLocationsController extends SubtreeController implements OnboardingLocationsActions {
  readonly state = new OnboardingLocationsState();

  constructor(
    readonly deps: OnboardingLocationsDeps,
    readonly params: OnboardingLocationsParams,
    readonly routing: OnboardingLocationsRouting,
  ) {
    super();
    this.subtree.put(OnboardingLocationsState, this.state);
    this.subtree.put(OnboardingLocationsActions, this);
    this.sync(() => {
      const storeId = deps.storeRepository.shopId;
      this.state.storeId.value = storeId;
      this.state.claimWalkthrough.value = params.claimOnboarding && params.storeIdParam === storeId;
    }, [deps.storeRepository]);
    this.sync(() => {
      this.state.locations.value = deps.locationsRepository.locations;
    }, [deps.locationsRepository]);
    this.sync(() => {
      const repo = deps.countriesRepository;
      if (repo.status.kind === "idle") void loadCountries(deps);
      this.state.countries.value = repo.countries;
      this.state.countriesStatus.value = repo.status;
    }, [deps.countriesRepository]);
    this.sync(() => {
      this.state.regions.value = deps.countriesRepository.regionsFor(this.state.draft.value.countryCode);
    }, [this.state.draft, deps.countriesRepository]);
    this.sync(() => {
      this.state.editRegions.value = deps.countriesRepository.regionsFor(this.state.editDraft.value.countryCode);
    }, [this.state.editDraft, deps.countriesRepository]);
  }

  setField = (field: LocationField, value: string) => {
    this.state.draft.update((d) => ({ ...d, [field]: value }));
    if (this.state.errors.value[field]) this.state.errors.update(({ [field]: _, ...rest }) => rest);
  };

  add = async () => {
    const storeId = this.state.storeId.value;
    if (!storeId || this.state.addForm.value.busy) return;
    const draft = this.state.draft.value;
    const errors = validateLocationDraft(draft, this.deps.countriesRepository.regionsFor(draft.countryCode));
    this.state.errors.value = errors;
    if (hasErrors(errors)) return;
    this.state.addForm.value = busyForm;
    const r = await createLocation({ ...this.deps, storeId, body: draftToNewLocation(draft) });
    if (this.isDisposed) return;
    if (!r.ok) {
      this.state.addForm.value = failedForm(r.errors);
      return;
    }
    this.state.draft.value = EMPTY_LOCATION;
    this.state.addForm.value = idleForm;
  };

  startEdit = (locationId: string) => {
    const location = this.state.locations.value.find((l) => l.id === locationId);
    if (!location) return;
    this.state.draft.value = EMPTY_LOCATION;
    this.state.errors.value = {};
    this.state.editingId.value = locationId;
    this.state.editDraft.value = locationDraft(location);
    this.state.editErrors.value = {};
    this.state.editForm.value = idleForm;
  };

  setEditField = (field: LocationField, value: string) => {
    this.state.editDraft.update((d) => ({ ...d, [field]: value }));
    if (this.state.editErrors.value[field]) this.state.editErrors.update(({ [field]: _, ...rest }) => rest);
  };

  saveEdit = async () => {
    const storeId = this.state.storeId.value;
    const locationId = this.state.editingId.value;
    if (!storeId || !locationId || this.state.editForm.value.busy) return;
    const draft = this.state.editDraft.value;
    const errors = validateLocationDraft(draft, this.deps.countriesRepository.regionsFor(draft.countryCode));
    this.state.editErrors.value = errors;
    if (hasErrors(errors)) return;
    this.state.editForm.value = busyForm;
    const r = await updateLocation({ ...this.deps, storeId, locationId, body: draftToLocationUpdate(draft) });
    if (this.isDisposed) return;
    if (!r.ok) {
      this.state.editForm.value = failedForm(r.errors);
      return;
    }
    this.state.editForm.value = idleForm;
    this.state.editingId.value = null;
  };

  cancelEdit = () => {
    this.state.editingId.value = null;
    this.state.editForm.value = idleForm;
  };

  back = () => {
    this.routing.toPrevious(this.state.storeId.value ?? "", this.state.claimWalkthrough.value);
  };

  next = () => {
    this.routing.toWebsite(this.state.storeId.value ?? "", this.state.claimWalkthrough.value);
  };
}
